import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const products = [
  {
    id: "ex10",
    name: "ExaGrid EX10",
    badge: "Entry",
    tagline: "Entry-Level Appliance — 10TB Full Backup, 2U",
    specs: [
      { label: "Full Backup Capacity", value: "10TB" },
      { label: "Raw Disk Capacity", value: "8 × 4TB = 32TB" },
      { label: "Form Factor", value: "2U Rack Appliance" },
      { label: "Memory", value: "16 GiB RAM" },
      { label: "CPU", value: "1 CPU, 4 cores" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25GbE" },
      { label: "Power Draw", value: "276W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "Cost-effective entry point for SMBs and branch offices",
      "Same enterprise-grade adaptive deduplication (avg 20:1) as larger models",
      "Scales up by adding any ExaGrid appliance model — mix and match supported",
      "Simple plug-and-play expansion — ExaGrid software virtualizes the capacity pool",
      "Supports all major backup applications out of the box",
      "IPv6 support and IPMI port for remote management",
    ],
  },
  {
    id: "ex18",
    name: "ExaGrid EX18",
    badge: "",
    tagline: "Small Business Workhorse — 18TB Full Backup, 2U",
    specs: [
      { label: "Full Backup Capacity", value: "18TB" },
      { label: "Raw Disk Capacity", value: "12 × 4TB = 48TB" },
      { label: "Form Factor", value: "2U Rack Appliance" },
      { label: "Memory", value: "32 GiB RAM" },
      { label: "CPU", value: "1 CPU, 4 hyper-threaded cores" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25/40GbE" },
      { label: "Power Draw", value: "318W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "18TB full backup in a space-efficient 2U chassis",
      "32 GiB RAM for solid deduplication throughput at this capacity tier",
      "Optional 10GbE, 25GbE, or 40GbE NIC for faster backup ingest",
      "Post-process adaptive deduplication — backup window never impacted",
      "Scales seamlessly with any other ExaGrid model in a single system",
      "FIPS 140-2 Self-Encrypting Drive (SED) option available",
    ],
  },
  {
    id: "ex27",
    name: "ExaGrid EX27",
    badge: "",
    tagline: "Mid-Range Efficiency — 27TB Full Backup, 2U",
    specs: [
      { label: "Full Backup Capacity", value: "27TB" },
      { label: "Raw Disk Capacity", value: "12 × 6TB = 72TB" },
      { label: "Form Factor", value: "2U Rack Appliance" },
      { label: "Memory", value: "32 GiB RAM" },
      { label: "CPU", value: "1 CPU, 6 hyper-threaded cores" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25/40GbE" },
      { label: "Power Draw", value: "381W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "27TB full backup capacity — ideal for growing mid-size organisations",
      "6 hyper-threaded cores deliver strong deduplication at this capacity tier",
      "12 × 6TB enterprise SAS drives with RAID6 protection",
      "Landing zone keeps most recent backup undeduplicated for instant VM recovery",
      "Non-network-facing repository blocks ransomware from reaching backup data",
      "Mix and match with EX10, EX36, EX52, EX84 in a single scale-out system",
    ],
  },
  {
    id: "ex36",
    name: "ExaGrid EX36",
    badge: "Popular",
    tagline: "High-Capacity Mid-Range — 36TB Full Backup, 2U",
    specs: [
      { label: "Full Backup Capacity", value: "36TB" },
      { label: "Raw Disk Capacity", value: "12 × 8TB = 96TB" },
      { label: "Form Factor", value: "2U Rack Appliance" },
      { label: "Memory", value: "64 GiB RAM" },
      { label: "CPU", value: "1 CPU, 12 hyper-threaded cores" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25/40GbE" },
      { label: "Power Draw", value: "417W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "36TB full backup capacity in a compact 2U chassis",
      "64 GiB RAM with 12 hyper-threaded cores for strong deduplication performance",
      "Optional 10GbE, 25GbE, or 40GbE NIC for high-speed backup ingest",
      "Adaptive deduplication runs post-process — never impacts backup window speed",
      "Scales seamlessly with other ExaGrid appliances in a single system",
      "FIPS 140-2 Self-Encrypting Drive (SED) option for SEC-compliant environments",
    ],
  },
  {
    id: "ex52",
    name: "ExaGrid EX52",
    badge: "",
    tagline: "Large Enterprise Node — 52TB Full Backup, 3U",
    specs: [
      { label: "Full Backup Capacity", value: "52TB" },
      { label: "Raw Disk Capacity", value: "16 × 8TB = 128TB" },
      { label: "Form Factor", value: "3U Rack Appliance" },
      { label: "Memory", value: "64 GiB RAM" },
      { label: "CPU", value: "1 CPU, 16 hyper-threaded cores" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25/40GbE" },
      { label: "Power Draw", value: "447W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "52TB full backup per node — ideal for large enterprise environments",
      "16 hyper-threaded cores deliver high deduplication throughput at scale",
      "16 × 8TB enterprise SAS drives in a 3U chassis",
      "64 GiB RAM supports large deduplication dictionaries for better ratios",
      "Scales to 2.7PB when combined with up to 32 appliances per system",
      "Immutable objects + retention time-lock for ransomware-proof backup storage",
    ],
  },
  {
    id: "ex84",
    name: "ExaGrid EX84",
    badge: "Flagship",
    tagline: "Largest Single Appliance — 84TB Full Backup, 4U",
    specs: [
      { label: "Full Backup Capacity", value: "84TB" },
      { label: "Raw Disk Capacity", value: "24 × 8TB = 192TB" },
      { label: "Form Factor", value: "4U Rack Appliance" },
      { label: "Memory", value: "128 GiB RAM" },
      { label: "CPU", value: "Dual-socket multi-core" },
      { label: "NIC Ports", value: "2× 1GbE + opt. 10/25/40GbE" },
      { label: "Power Draw", value: "590W" },
      { label: "Drive Protection", value: "RAID6 + hot spare, redundant PSU & fans" },
    ],
    features: [
      "Largest single ExaGrid appliance — 84TB full backup per node",
      "128 GiB RAM with dual-socket multi-core CPU for maximum deduplication throughput",
      "RAID6 with hot spare guards against up to two simultaneous disk failures",
      "Landing zone keeps most recent backup in full, undeduplicated form for instant VM recovery",
      "Non-network-facing repository eliminates ransomware attack surface",
      "Mix and match with older appliance models — no product obsolescence",
    ],
  },
];

const allModels = [
  {
    model: "EX10",
    badge: "",
    fullBackup: "10TB",
    chassis: "2U",
    disks: "8",
    diskSize: "4TB",
    memory: "16 GiB",
    cpu: "1 CPU, 4 cores",
    nic: "2× 1GbE + opt. 10/25GbE",
    power: "276W",
  },
  {
    model: "EX18",
    badge: "",
    fullBackup: "18TB",
    chassis: "2U",
    disks: "12",
    diskSize: "4TB",
    memory: "32 GiB",
    cpu: "1 CPU, 4 HT cores",
    nic: "2× 1GbE + opt. 10/25/40GbE",
    power: "318W",
  },
  {
    model: "EX27",
    badge: "",
    fullBackup: "27TB",
    chassis: "2U",
    disks: "12",
    diskSize: "6TB",
    memory: "32 GiB",
    cpu: "1 CPU, 6 HT cores",
    nic: "2× 1GbE + opt. 10/25/40GbE",
    power: "381W",
  },
  {
    model: "EX36",
    badge: "Popular",
    fullBackup: "36TB",
    chassis: "2U",
    disks: "12",
    diskSize: "8TB",
    memory: "64 GiB",
    cpu: "1 CPU, 12 HT cores",
    nic: "2× 1GbE + opt. 10/25/40GbE",
    power: "417W",
  },
  {
    model: "EX52",
    badge: "",
    fullBackup: "52TB",
    chassis: "3U",
    disks: "16",
    diskSize: "8TB",
    memory: "64 GiB",
    cpu: "1 CPU, 16 HT cores",
    nic: "2× 1GbE + opt. 10/25/40GbE",
    power: "447W",
  },
  {
    model: "EX84",
    badge: "Flagship",
    fullBackup: "84TB",
    chassis: "4U",
    disks: "24",
    diskSize: "8TB",
    memory: "128 GiB",
    cpu: "Dual-socket multi-core",
    nic: "2× 1GbE + opt. 10/25/40GbE",
    power: "590W",
  },
];

const whyExaGrid = [
  { icon: "ri-speed-line", title: "Fastest Backup & Recovery", desc: "ExaGrid's landing zone keeps recent backups in full, undeduplicated form — enabling instant VM recovery without waiting for rehydration." },
  { icon: "ri-shield-keyhole-line", title: "Ransomware-Proof Architecture", desc: "Non-network-facing repository with immutable objects and retention time-lock means ransomware cannot encrypt or delete your backups." },
  { icon: "ri-add-circle-line", title: "True Scale-Out Design", desc: "Add appliances as data grows — each new node adds both capacity and performance. No forklift upgrades, ever." },
  { icon: "ri-money-dollar-circle-line", title: "50% Lower Total System Cost", desc: "ExaGrid's scale-out architecture eliminates costly forklift upgrades. Customers pay as they grow — newer appliances mix with older models, protecting the IT investment over time." },
  { icon: "ri-plug-line", title: "Works with 25+ Backup Applications", desc: "Native integration with Veeam, Commvault, Veritas NetBackup, IBM Spectrum Protect (TSM), Dell EMC NetWorker, Arcserve, Oracle RMAN, and more." },
  { icon: "ri-customer-service-2-line", title: "Local Authorized Support", desc: "SE Distribution provides full local warranty, deployment support, and ongoing maintenance from our Kuala Lumpur office." },
];

const useCases = [
  {
    icon: "ri-bank-line",
    title: "Financial Services",
    desc: "Banks and insurance firms use ExaGrid to meet strict RTO/RPO requirements and regulatory data retention mandates — with immutable backups that satisfy audit requirements.",
    stat: "99.99% recovery success rate",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare",
    desc: "Hospitals and clinics protect patient records and PACS imaging data with ExaGrid's instant recovery and long-term retention — ensuring continuity of care even after a ransomware attack.",
    stat: "Sub-15 minute VM recovery",
  },
  {
    icon: "ri-government-line",
    title: "Government & Public Sector",
    desc: "Government agencies rely on ExaGrid for compliant, long-term data retention with immutable storage that meets public sector data governance requirements.",
    stat: "100% audit trail compliance",
  },
  {
    icon: "ri-building-2-line",
    title: "Enterprise IT",
    desc: "Large enterprises with multi-petabyte backup environments use ExaGrid's scale-out architecture to grow without disruption — adding appliances as data volumes increase.",
    stat: "Up to 2.7PB per system",
  },
];

const integrations = [
  "Veeam Backup & Replication",
  "Commvault",
  "Veritas NetBackup",
  "Veritas Backup Exec",
  "IBM Spectrum Protect (TSM)",
  "Dell EMC NetWorker",
  "Arcserve Backup, D2D & UDP",
  "Micro Focus Data Protector",
  "Quest NetVault & vRanger",
  "StorageCraft ShadowProtect",
  "Bacula Systems",
  "Idera SQL Safe",
  "Oracle RMAN",
  "Zerto Offsite Backup",
  "HYCU",
  "BridgeHead",
];

const exagridBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.sedistribution.com.my/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Solutions",
      "item": "https://www.sedistribution.com.my/solutions"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Enterprise Backup & Recovery",
      "item": "https://www.sedistribution.com.my/solutions/data-protection"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "ExaGrid Backup Spotlight",
      "item": "https://www.sedistribution.com.my/exagrid-spotlight"
    }
  ]
};

export default function ExaGridSpotlightPage() {
  useSEO({
    title: "ExaGrid Backup Appliance Malaysia | Ransomware-Proof, Instant VM Recovery | SE Distribution",
    description: "ExaGrid backup storage Malaysia — fastest recovery with landing zone, non-network-facing ransomware protection, 10–50x deduplication, scale-out to 2.7PB. Authorized distributor in Kuala Lumpur.",
    canonical: "https://www.sedistribution.com.my/exagrid-spotlight",
    keywords: "ExaGrid Malaysia, backup appliance Malaysia, ransomware protection backup Malaysia, instant VM recovery, ExaGrid distributor Malaysia, enterprise backup Malaysia, deduplication storage",
    structuredData: exagridBreadcrumbSchema,
  });
  const [activeProduct, setActiveProduct] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StickyQuoteButton label="Request a Demo" />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=enterprise%20data%20center%20server%20room%20rows%20of%20rack%20servers%20glowing%20LED%20lights%20dark%20professional%20environment%2C%20dramatic%20cinematic%20lighting%2C%20wide%20angle%20shot%2C%20high%20detail%2C%20modern%20technology%20infrastructure&width=1400&height=700&seq=eg-hero1&orientation=landscape"
            alt="ExaGrid Backup Solutions"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-[#1a1a1a]/30"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/332e9681-9df1-4dcd-93f2-cf1098c6260f_exagrid-logo.png?v=ddd934934df6f2e00eabba5539238300"
                  alt="ExaGrid"
                  className="h-8 w-auto brightness-0 invert"
                />
                <span className="text-white/40 text-sm">|</span>
                <span className="text-xs font-semibold tracking-widest uppercase text-white/50">Enterprise Backup Solutions</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
                The Smarter Way to <strong>Protect Your Backups</strong>
              </h1>
              <p className="text-white/65 text-base leading-relaxed mb-8">
                ExaGrid is the only backup storage solution with a disk landing zone for fastest backup and recovery, scale-out architecture that eliminates forklift upgrades, and adaptive deduplication averaging 20:1 (up to 50:1) — scaling from 6TB to 2.7PB full backup with up to 32 appliances per system.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Fastest Recovery</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Ransomware-Proof</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">True Scale-Out</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">10–50x Deduplication</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Request a Demo
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="tel:+60364127917"
                  className="border border-white/40 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  +603 6412 7917
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "10–50x", label: "Data Deduplication Ratio (avg. 20:1)" },
              { value: "2.7PB", label: "Max Full Backup Per System" },
              { value: "6 Models", label: "Appliance Sizes (EX10–EX84)" },
              { value: "32", label: "Max Appliances Per System" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</p>
                <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why ExaGrid */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Why ExaGrid</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Purpose-Built for Backup — Unlike Anything Else</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">ExaGrid is not a general-purpose storage array repurposed for backup. It is engineered from the ground up to solve the three biggest backup challenges: speed, ransomware, and scale.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {whyExaGrid.map((w) => (
                <div key={w.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                    <i className={`${w.icon} text-[#1a1a1a] text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{w.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Lineup */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Product Lineup</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">ExaGrid Appliance Range</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">ExaGrid offers 6 appliance models (EX10, EX18, EX27, EX36, EX52, EX84) ranging from 10TB to 84TB per node. Mix and match models in a single scale-out system of up to 32 appliances — scaling from 6TB to 2.7PB full backup.</p>
          </div>

          {/* Product Tabs */}
          <div className="flex gap-2 justify-center mb-10 flex-wrap">
            {products.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(i)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeProduct === i
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {p.name}
                {p.badge === "Flagship" && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Flagship</span>
                )}
              </button>
            ))}
          </div>

          {/* Active Product Detail */}
          {products.map((p, i) => (
            <div
              key={p.id}
              className={`flex flex-col lg:flex-row gap-12 items-start ${activeProduct === i ? "block" : "hidden"}`}
            >
              <div className="w-full">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    p.badge === "Flagship" ? "bg-amber-100 text-amber-700" :
                    p.badge === "Popular" ? "bg-green-100 text-green-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{p.badge}</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-1">{p.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{p.tagline}</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {p.specs.map((s) => (
                    <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                      <p className="text-sm font-semibold text-[#1a1a1a]">{s.value}</p>
                    </div>
                  ))}
                </div>
                <div className="mb-7">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Key Features</p>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Link
                    to="/contact"
                    className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enquire About {p.name}
                  </Link>
                  <Link
                    to="/brands"
                    className="border border-gray-200 text-gray-600 text-sm font-medium px-6 py-3 rounded-md hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View ExaGrid Brand Profile
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Full Model Comparison Table */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Technical Specifications</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Full Appliance Comparison — All 6 Models</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">
                All appliances use Enterprise SAS drives, RAID6 with hot spare, redundant fans and power supplies, IPMI port, and IPv6. FIPS 140-2 Self-Encrypting Drive (SED) option available on all models. Scale-out up to 32 appliances per system — supporting 6TB to 2.7PB full backup.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-[#1a1a1a]">
                    <th className="text-left text-xs font-bold text-white/60 uppercase tracking-wider px-5 py-4">Specification</th>
                    {allModels.map((m) => (
                      <th key={m.model} className="text-center px-4 py-4">
                        <div className="flex flex-col items-center gap-1">
                          {m.badge && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              m.badge === "Flagship" ? "bg-amber-400/20 text-amber-300" : "bg-green-400/20 text-green-300"
                            }`}>
                              {m.badge}
                            </span>
                          )}
                          <span className="text-sm font-bold text-white">{m.model}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { label: "Full Backup Capacity", key: "fullBackup", highlight: true },
                    { label: "Chassis", key: "chassis", highlight: false },
                    { label: "Drive Count", key: "disks", highlight: false },
                    { label: "Drive Size", key: "diskSize", highlight: false },
                    { label: "Memory (RAM)", key: "memory", highlight: false },
                    { label: "CPU", key: "cpu", highlight: false },
                    { label: "NIC Ports", key: "nic", highlight: false },
                    { label: "Power Draw", key: "power", highlight: false },
                  ].map((row, ri) => (
                    <tr key={row.label} className={ri % 2 === 0 ? "bg-white" : "bg-[#fafaf9]"}>
                      <td className="px-5 py-3.5 text-xs font-semibold text-[#1a1a1a] whitespace-nowrap">{row.label}</td>
                      {allModels.map((m) => (
                        <td
                          key={m.model}
                          className={`px-4 py-3.5 text-xs text-center ${
                            row.highlight
                              ? "font-bold text-[#1a1a1a]"
                              : "text-gray-600"
                          } ${m.badge === "Flagship" ? "bg-amber-50/50" : m.badge === "Popular" ? "bg-green-50/50" : ""}`}
                        >
                          {m[row.key as keyof typeof m]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex flex-wrap gap-6 justify-center">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-200 inline-block"></span>
                Flagship model
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3 h-3 rounded-sm bg-green-50 border border-green-200 inline-block"></span>
                Most popular
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <i className="ri-information-line text-gray-400"></i>
                All models support RAID6 + hot spare, redundant PSU, FIPS 140-2 SED option, IPv6, IPMI
              </div>
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                Get a Sizing Recommendation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 block">Real-World Applications</span>
              <h2 className="text-3xl font-bold text-white">Who Uses ExaGrid in Malaysia</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u) => (
                <div key={u.title} className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-4">
                    <i className={`${u.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-2">{u.title}</h3>
                  <p className="text-white/55 text-xs leading-relaxed mb-4">{u.desc}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-xs font-semibold text-white/70">{u.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/2 h-72 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=IT%20administrator%20managing%20backup%20software%20dashboard%20multiple%20monitors%20server%20room%20enterprise%20data%20management%20system%2C%20professional%20environment%2C%20dramatic%20lighting%2C%20photorealistic&width=700&height=450&seq=eg-integrations1&orientation=landscape"
                alt="ExaGrid Integrations"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Ecosystem Compatibility</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-5">Works with Your Existing Backup Software</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                ExaGrid integrates natively with over 25 backup software applications — no rip-and-replace of your existing backup infrastructure. Simply add ExaGrid as your backup storage target and immediately benefit from faster backups, instant VM recovery, and ransomware protection. ExaGrid also supports POSIX-compliant TAR files, Oracle RMAN channels, and SQL Dumps.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-7">
                {integrations.map((int) => (
                  <div key={int} className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-check-line text-[#1a1a1a] flex-shrink-0"></i>
                    {int}
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                Check Compatibility for Your Environment
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* ExaGrid Backup Malaysia CTA Band */}
        <section className="py-10 bg-[#f9f7f4] border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-xl flex-shrink-0">
                <i className="ri-shield-keyhole-line text-white text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-0.5">Sizing &amp; Pricing</p>
                <h3 className="text-base font-bold text-[#1a1a1a]">
                  Ready to deploy ExaGrid in Malaysia? Get a full ransomware-proof backup assessment.
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Includes appliance sizing, TCO vs tape &amp; cloud, and a 3-year cost comparison — no obligation.
                </p>
              </div>
            </div>
            <Link
              to="/exagrid-backup-malaysia"
              className="flex-shrink-0 bg-[#1a1a1a] text-white text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              View ExaGrid Backup Malaysia
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20data%20center%20server%20room%20professional%20enterprise%20technology%20infrastructure%2C%20dramatic%20lighting%2C%20dark%20atmosphere%2C%20wide%20shot%2C%20cinematic%20photography%2C%20high%20detail&width=1400&height=500&seq=eg-cta1&orientation=landscape"
            alt="Deploy ExaGrid"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 to-[#1a1a1a]/95"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Protect Your Backups with ExaGrid?</h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
              As Malaysia&apos;s authorized ExaGrid distributor, SE Distribution handles everything from sizing and procurement to deployment, integration, and ongoing support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/exagrid-backup-malaysia"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                Get a Sizing Assessment
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call +603 6412 7917
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
